import {
  Badge,
  Checkbox,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ActionsDialog from "../actionDialogs";
import ActionsCell from "../actionsCell";
import { APPOINTMENT, ATTENDED, CONFIRM, dateFilters, languagesMaps, priorityNames , SURGERY_BOOKING_SOURCE, SURGERY_DISPLAY_NAME } from "./constants";
import RescheduleDialog from "../rescheduleDialog";
import DocumentUploadDialog from "../documentUploadDialog";
import BulkActionsDialog from "../bulkActionDialog";
import DownloadIcon from "@mui/icons-material/Download";
import { fetchAdditionalInfo } from "./apiCalls";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { HowToUse } from "./howToUseDialog";
import { EVENTS } from "../events_constant";
import { logEventToAnalyticsPlatform } from "../../../helper/events";
import { otpEnabledAttendanceProvider } from "../../../auth/common";

export const TableStructure = ({
  orders,
  totalRows,
  page,
  setPage,
  subTabs,
  selectedSubTab,
  setSelectedSubTab,
  loading,
  exportLoading,
  fetchData,
  setFetchData,
  actionsDisable,
  remarksEnable,
  checkBox,
  bulkMarkStatus,
  exportOrderData,
  sortDirection,
  setSortDirection,
  setSortLabel,
  checkSlaOnModifiedDate,
}) => {
  const languagesMap = languagesMaps();
  const [action, setAction] = useState("");
  const [actionDialogVisible, setActionDialogVisibility] = useState(false);
  const dateFilterObject = JSON.parse(sessionStorage.getItem("dateFilterObject"));
  const dateFilterName = dateFilterObject?.name ? dateFilterObject.name : "Rolling 1 Month";
  const [dateFilter, setDateFilter] = useState(dateFilterName);
  const selectedDateFilter = dateFilters.find((elem) => elem.name === dateFilterName);
  const [startDate, setStartDate] = useState(dateFilterObject?.startDate ? dateFilterObject.startDate : selectedDateFilter.startDate);
  const [endDate, setEndDate] = useState(dateFilterObject?.endDate ? dateFilterObject.endDate : selectedDateFilter.endDate);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [rescheduleDialogVisible, setRescheduleDialogVisibility] = useState(false);
  const [docUploadDialogVisible, setDocUploadDialogVisibility] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkActionDialogVisible, setBulkActionDialogVisibility] = useState(false);
  const [howToUseDialogVisible, setHowToUseDialogVisibiliy] = useState(false);
  const [actionHeader, setActionHeader] = useState("");
  const [properties, setProperties] = useState(null);
  const user_details = JSON.parse(localStorage.getItem("user_details"));
  const childEntities = user_details?.parentProvider?.isParent ? user_details.parentProvider.childEntities : null;
  const [language, setLanguage] = useState("English");
  const [videoURL, setVideoURL] = useState(languagesMap["English"]);
  const totalRowsToSelect = totalRows && totalRows < 10 ? totalRows : 10;
  const isOtpEnabledAttendanceProvider = otpEnabledAttendanceProvider();

  const onDateChange = (selectedValue) => {
    setDateFilter(selectedValue);
    const selectedDateFilter = dateFilters.find((elem) => elem.name === selectedValue);
    if (selectedDateFilter && selectedDateFilter.startDate && selectedDateFilter.endDate) {
      setStartDate(selectedDateFilter.startDate);
      setEndDate(selectedDateFilter.endDate);
      let dateFilterObject = {
        name: selectedValue,
      };
      sessionStorage.setItem("dateFilterObject", JSON.stringify(dateFilterObject));
      setFetchData(!fetchData);
    } else {
      setStartDate("");
      setEndDate("");
    }
  };
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth || dateOfBirth === "/Date(-62135596800000-0000)/" || dateOfBirth.match("000")) return "NA";

    const today = new Date();
    const dob = new Date(dateOfBirth);

    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    return !Number(age) ? "NA" : age;
  };

  const handleChangePage = (event, newPage) => {
    setFetchData(!fetchData);
    setPage(newPage);
  };

  const handleClickSubTab = (elem) => {
    setSelectedSubTab(elem.value);
    setFetchData(!fetchData);
  };

  const handleClickSearch = () => {
    let dateFilterObject = {
      name: dateFilter,
      startDate: startDate,
      endDate: endDate,
    };
    sessionStorage.setItem("dateFilterObject", JSON.stringify(dateFilterObject));
    setFetchData(!fetchData);
    logEventToAnalyticsPlatform({
      name: EVENTS.PC_LS_ACTION.name,
      payload: {
        tab_name: EVENTS.TAB_NAME_UPCOMING_APPOINTMENTS,
        filterValue: 'true',
        reference_id: '',
        reference_provider_id: '',
        service_name: EVENTS.SERVICE_NAME_OFFLINE_CONSULTS,
        action: EVENTS.ACTION.SEARCH_ICON_CLICKED,
      },
      schema: EVENTS.PC_LS_ACTION.schema,
    })
  };

  const handleCheckboxClick = (requestId) => {
    const ids = [...selectedIds];
    if (ids.includes(requestId)) {
      ids.splice(ids.indexOf(requestId), 1);
    } else {
      ids.push(requestId);
    }
    setSelectedIds(ids);
  };

  const handleSelectAllClick = (evt) => {
    if (evt.target.checked) {
      setSelectedIds(orders.map((elem) => elem.id));
    } else {
      setSelectedIds([]);
    }
  };

  const bulkConfirmClick = () => {
    setAction(CONFIRM);
    setActionHeader("Bulk Confirm");
    setBulkActionDialogVisibility(true);
  };

  const bulkAttendClick = () => {
    setAction(ATTENDED);
    setActionHeader("Bulk Mark Attended");
    setBulkActionDialogVisibility(true);
  };

  const handleRequestSort = (sortLabel) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSortLabel(sortLabel);
    setFetchData(!fetchData);
  };

  const fetchAdditionalDetails = async () => {
    let requestIds = orders.map((ord) => ord.id);
    let response = await fetchAdditionalInfo(["OFFLINE_CONSULTS"], requestIds);
    if (response?.success) {
      setProperties(response.data);
    }
  };

  useEffect(() => {
    if (orders?.length) {
      fetchAdditionalDetails();
    }
  }, [loading]);

  const getDoctorPrefix = (doctorName)=> {
    const doctorNameSplit = doctorName?.split(" ");
    if (doctorNameSplit.length > 1) {
      if (doctorNameSplit[0].toLowerCase() == 'clove' ||
           doctorNameSplit[0].toLowerCase() == 'dr.' ||
              doctorNameSplit[0].toLowerCase() == "dr")
                   return '';
      else {
        return 'Dr.';
      }
    }
  
  }

  const headCells = [
    {
      id: "requestid",
      label: "Request ID/ Member ID",
      sortEnabled: false,
    },
    {
      id: "customername",
      label: "Customer Details",
      sortEnabled: false,
    },
    {
      id: "corporatename",
      label: "Address/ Contact No.",
      sortEnabled: false,
    },
    {
      id: "speciality",
      label: "Speciality",
      sortEnabled: false,
    },
    {
      id: "doctorname",
      label: "Doctor's Name",
      sortEnabled: false,
    },
    {
      id: "appointmentdate",
      label: "Appointment Date & Time",
      sortEnabled: true,
      sortLabel: "FulfillmentDate",
    },
    {
      id: "modifieddate",
      label: "Modified Date & Time",
      sortEnabled: true,
      sortLabel: "ModificationDate",
    },
    {
      id: "consultationfee",
      label: "Consultation Fee",
      sortEnabled: false,
    },
    {
      id: "status",
      label: "Status",
      sortEnabled: false,
    },
    {
      id: "priority",
      label: "Priority",
      sortEnabled: true,
      sortLabel: "Priority",
    },
  ];
  if (!actionsDisable) {
    headCells.push({
      id: "additionals",
      label: "Actions",
    });
  }
  if (remarksEnable) {
    headCells.push({
      id: "remarks",
      label: "Remarks",
    });
  }

  return (
    <>
      <div className="flex w-full justify-between pb-4 px-4">
        <div className="flex date-filter-dashboard">
          <FormControl sx={{ width: 140 }} size="small">
            <InputLabel id="dateFilter">Appointment Date</InputLabel>
            <Select
              labelId="dateFilter"
              name="dateFilter"
              id="dateFilter"
              value={dateFilter}
              label="Appointment Date"
              onChange={(event) => {
                onDateChange(event.target.value);
              }}
            >
              {dateFilters.map((elem) => (
                <MenuItem value={elem.name}>{elem.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="ml-2">
            <TextField
              className="w-full"
              id="date"
              label="Start Date"
              type="date"
              value={startDate ? startDate : ""}
              onChange={(evt) => setStartDate(evt.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={dateFilter === "Custom" ? false : true}
            />
          </div>
          <div className="ml-2">
            <TextField
              className="w-full"
              id="date"
              label="End Date"
              type="date"
              value={endDate ? endDate : ""}
              onChange={(evt) => setEndDate(evt.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={dateFilter === "Custom" ? false : true}
            />
          </div>
          <div className="ml-2 search-icon">
            <div className="w-full">
              <div className="m-auto">
                <SearchIcon onClick={() => handleClickSearch()} />
              </div>
            </div>
          </div>
        </div>
        <div className="m-auto">
          <div className="w-full flex text-center text-xs">
            {subTabs?.length > 0
              ? subTabs.map((elem, index) => (
                  <div
                    className={
                      elem.value === selectedSubTab
                        ? "mr-hr-xs-0 border-2 border-blue-500 text-gray-900 font-semibold text-center rounded-md shadow-inner text-xs cursor-pointer mx-2 h-full"
                        : "mr-hr-xs-0 border-2 border-gray-200 text-gray-900 font-semibold text-center rounded-md shadow-md text-xs cursor-pointer mx-2 h-full"
                    }
                    key={index}
                    onClick={() => handleClickSubTab(elem)}
                  >
                    <div className="p-2 px-5">
                      <span className="mr-2">{elem.label}</span>
                    </div>
                  </div>
                ))
              : ""}
          </div>
        </div>
      </div>
      <div className="mb-4 px-4 flex justify-between">
        <div className="flex">
          <Select
            className="mr-2"
            sx={{
              display: "inline-block",
              fontSize: "13px",
              boxShadow: "none",
            }}
            labelId="language"
            name="language"
            id="language"
            value={language}
            onChange={(event) => {
              setLanguage(event.target.value);
              setVideoURL(languagesMap[event.target.value]);
            }}
            size="small"
            placeholder="Select Language"
            required
          >
            {Object.keys(languagesMap).map((ele) => (
              <MenuItem sx={{ fontSize: "13px", padding: "5px" }} value={ele}>
                {ele}
              </MenuItem>
            ))}
          </Select>
          <button
            className="border border-blue-400  hover:bg-blue-600 hover:border-blue-600 hover:text-gray-100 text-gray-900 font-semibold text-center rounded-md py-2 px-3 text-xs cursor-pointer mr-4 my-auto"
            onClick={() => setHowToUseDialogVisibiliy(true)}
          >
            <span className="pr-1 font-svg-xxs">
              <PlayCircleIcon></PlayCircleIcon>
            </span>
            How to Use
          </button>
        </div>
        <div className="flex">
          {bulkMarkStatus === CONFIRM && checkBox && (
            <>
              {selectedIds && selectedIds.length > 1 ? (
                <div className="my-auto" onClick={() => bulkConfirmClick()}>
                  <Badge badgeContent={selectedIds.length} color="primary">
                    <span className="bg-blue-100 text-gray-900 font-semibold border text-center rounded-md py-2 px-3 text-xs cursor-pointer m-auto">
                      Bulk Confirm
                    </span>
                  </Badge>
                </div>
              ) : (
                <div className="bg-gray-200 text-gray-900 font-semibold border text-center rounded-md py-2 px-3 text-xs cursor-disabled my-auto">
                  Bulk Confirm
                </div>
              )}
            </>
          )}
          {bulkMarkStatus == ATTENDED && !isOtpEnabledAttendanceProvider &&  checkBox && (
            <>
              {selectedIds && selectedIds.length > 1 ? (
                <div className="my-auto" onClick={() => bulkAttendClick()}>
                  <Badge badgeContent={selectedIds.length} color="primary">
                    <span className="bg-blue-100 text-gray-900 font-semibold border text-center rounded-md py-2 px-3 text-xs cursor-pointer my-auto">
                      Bulk Attend
                    </span>
                  </Badge>
                </div>
              ) : (
                <div className="bg-gray-200 text-gray-900 font-semibold border text-center rounded-md py-2 px-3 text-xs cursor-disabled my-auto">
                  Bulk Attend
                </div>
              )}
            </>
          )}
          <button
            className={
              exportLoading
                ? "border border-gray-400  hover:bg-gray-600 hover:border-gray-600 hover:text-gray-100 text-gray-900 font-semibold text-center rounded-md py-2 px-3 text-xs cursor-default ml-2 my-auto"
                : "border border-blue-400  hover:bg-blue-600 hover:border-blue-600 hover:text-gray-100 text-gray-900 font-semibold text-center rounded-md py-2 px-3 text-xs cursor-pointer ml-2 my-auto"
            }
            onClick={() => exportOrderData()}
            disabled={exportLoading}
          >
            Export
            <span className="pl-1 font-svg-xxs">
              <DownloadIcon></DownloadIcon>
            </span>
          </button>
        </div>
      </div>

      <div className="appointment-wrapper">
        {loading ? <LinearProgress /> : <div className="h-1"></div>}
        <TableContainer>
          <Table sx={{ minWidth: 750 }} className="text-sm" aria-labelledby="tableTitle" size={"medium"}>
            <TableHead>
              <TableRow className="bg-gray-100">
                {checkBox && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selectedIds.length > 0 && selectedIds.length < totalRowsToSelect}
                      checked={totalRowsToSelect > 0 && selectedIds.length === totalRowsToSelect}
                      onChange={(evt) => handleSelectAllClick(evt)}
                      inputProps={{
                        "aria-label": "select all desserts",
                      }}
                    />
                  </TableCell>
                )}
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id} align="left" padding="normal">
                    {headCell.sortEnabled ? (
                      <TableSortLabel direction={sortDirection} onClick={() => handleRequestSort(headCell.sortLabel)}>
                        <span className="font-bold text-xs">{headCell.label}</span>
                      </TableSortLabel>
                    ) : (
                      <span className="font-bold text-xs">{headCell.label}</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders?.length > 0
                ? orders.map((order, index) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const isItemSelected = selectedIds.includes(order.id);
                    const filteredProperties = properties?.filter((prop) => prop.requestId == order.id);
                    const requiredProperties = filteredProperties && filteredProperties.length > 0 && filteredProperties[0].properties;
                    return (
                      <TableRow
                        hover
                        // role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={order.id}
                        selected={isItemSelected}
                        className="bg-white"
                      >
                        {checkBox && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              onClick={(event) => handleCheckboxClick(order.id)}
                              checked={isItemSelected}
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell align="left" component="th" id={labelId} scope="row">
                          <div className="inline-flex items-center rounded-full bg-blue-500 px-3 py-1 text-xs font-medium text-white">
                            {order.BookingSource === SURGERY_BOOKING_SOURCE ? SURGERY_DISPLAY_NAME : order.fulfillmentType}
                          </div>                          <div className="text-gray-500">{order.parentRequestID && order.parentRequestID != 0 ? order.parentRequestID : order.id}</div>
                          <div className="text-gray-500 text-xs">{order.employeeId}</div>
                        </TableCell>
                        <TableCell align="left">
                          <strong>{order.patientName}</strong>
                          <div className="text-gray-500 text-xs">Age: {calculateAge(order.dob ?? "")}</div>
                          <div className="text-gray-500 text-xs">Gender: {order.gender}</div>
                        </TableCell>
                        <TableCell align="left">
                          <div className="text-sm">{order.addressLine1}</div>
                          <div className="text-gray-500 text-xs">{order.phone}</div>
                        </TableCell>
                        <TableCell align="left" className="cursor-pointer" style={{ maxWidth: "15vw" }}>
                          <div className="text-xs text-gray-500">{order.packageName}</div>
                        </TableCell>
                        <TableCell align="left">
                          <strong className="text-xs"> {getDoctorPrefix(order?.doctorName)} {order.doctorName}</strong>
                          {childEntities && <div className="text-xs text-gray-500">{childEntities[order.currentProvider]}</div>}
                        </TableCell>
                        <TableCell align="left">
                          <div className="text-xs">
                            {moment(order.fulfillmentDate).format("MMMM Do YYYY")}
                            <div className="text-gray-500 text-xs">
                              {order.fulfillmentType.toLowerCase() ==  APPOINTMENT &&
                                moment(order.fulfillmentDate).format("hh:mm A")
                              }
                              </div>
                            {requiredProperties?.Reschedule_By_Provider_Count > 0 && (
                              <div className="rounded-full p-1" style={{ background: "#FFF3DF", width: "fit-content", color: "#FFA000" }}>
                                Reschedule ({requiredProperties.Reschedule_By_Provider_Count})
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell align="left">
                          <div className="text-xs">
                            {moment(order.modifiedOn).format("MMMM Do YYYY")}
                            <div className="text-gray-500 text-xs">{moment(order.modifiedOn).format("hh:mm A")}</div>
                          </div>
                        </TableCell>
                        <TableCell align="left">
                          <div className="text-xs text-gray-500">{order.consultationFee ?? order.totalAmount}</div>
                        </TableCell>
                        <TableCell align="left">
                          <div className="text-xs text-gray-500">{order.displayStatus}</div>
                        </TableCell>
                        <TableCell align="left">
                          <div className="text-xs text-gray-500">{priorityNames[order.priorityId]}</div>
                        </TableCell>
                        {remarksEnable && (
                          <TableCell align="left">
                            <div className="text-xs">
                              {requiredProperties?.Cancellation_Reason ? <div>{requiredProperties.Cancellation_Reason}</div> : "No Remarks"}
                            </div>
                          </TableCell>
                        )}
                        {!actionsDisable && (
                          <TableCell align="left">
                            <ActionsCell
                              nextActions={order.nextActions}
                              currentOrder={order}
                              rescheduleDisable={
                                requiredProperties?.Reschedule_By_Provider_Count && requiredProperties?.Max_Reschedule_Count
                                  ? requiredProperties.Reschedule_By_Provider_Count >= requiredProperties.Max_Reschedule_Count
                                  : false
                              }
                              setActionDialogVisibility={setActionDialogVisibility}
                              setRescheduleDialogVisibility={setRescheduleDialogVisibility}
                              setDocUploadDialogVisibility={setDocUploadDialogVisibility}
                              setAction={setAction}
                              checkSlaOnModifiedDate={checkSlaOnModifiedDate}
                              setCurrentOrder={setCurrentOrder}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                : null}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack spacing={2} className="p-2">
          <div className="flex justify-between">
            <div className="pt-2">
              {orders?.length > 0 && (
                <div>
                  <span className="text-gray-400 text-sm">Total Counts :</span>
                  <span className="text-gray-400 pl-2 font-semibold text-sm">{totalRows}</span>
                </div>
              )}
            </div>

            <div>
              <Pagination
                count={parseInt(totalRows / 10) + 1}
                boundaryCount={4}
                showFirstButton={true}
                showLastButton={true}
                page={page}
                onChange={handleChangePage}
              />
            </div>
          </div>
        </Stack>
      </div>
      {actionDialogVisible && (
        <ActionsDialog
          action={action}
          appointmentDate={currentOrder.fulfillmentDate}
          actionDialogVisible={actionDialogVisible}
          setActionDialogVisibility={setActionDialogVisibility}
          requestId={currentOrder.id}
          speciality={currentOrder.packageName}
          patientName={currentOrder.patientName}
          fetchData={fetchData}
          setFetchData={setFetchData}
          currentProvider={currentOrder.currentProvider}
          pmEntityId={currentOrder.PMEntityId}
          otpEnabledAttendance={currentOrder.otpEnabledAttendance}
        ></ActionsDialog>
      )}
      {rescheduleDialogVisible && (
        <RescheduleDialog
          appointmentDate={currentOrder.fulfillmentDate}
          rescheduleDialogVisible={rescheduleDialogVisible}
          setRescheduleDialogVisibility={setRescheduleDialogVisibility}
          requestId={currentOrder.id}
          orderId={currentOrder.orderId}
          speciality={currentOrder.packageName}
          patientName={currentOrder.patientName}
          doctorName={currentOrder.doctorName}
          doctorId={currentOrder.doctorId}
          packageId={currentOrder.orderItemProductId}
          contractId={currentOrder.contractId}
          currentProvider={currentOrder?.currentProvider}
          pmEntityId={currentOrder?.PMEntityId}
          fetchData={fetchData}
          setFetchData={setFetchData}
        ></RescheduleDialog>
      )}
      {docUploadDialogVisible && (
        <DocumentUploadDialog
          appointmentDate={currentOrder.fulfillmentDate}
          docUploadDialogVisible={docUploadDialogVisible}
          setDocUploadDialogVisibility={setDocUploadDialogVisibility}
          requestId={currentOrder.id}
          speciality={currentOrder.packageName}
          patientName={currentOrder.patientName}
          fetchData={fetchData}
          setFetchData={setFetchData}
          contractId={currentOrder.contractId}
          currentProvider={currentOrder?.currentProvider}
          pmEntityId={currentOrder?.PMEntityId}
        ></DocumentUploadDialog>
      )}
      {bulkActionDialogVisible && (
        <BulkActionsDialog
          action={action}
          actionHeader={actionHeader}
          bulkActionDialogVisible={bulkActionDialogVisible}
          setBulkActionDialogVisibility={setBulkActionDialogVisibility}
          requestIds={selectedIds}
          fetchData={fetchData}
          setFetchData={setFetchData}
          setSelectedIds={setSelectedIds}
          service={["OFFLINE_CONSULTS"]}
        ></BulkActionsDialog>
      )}

      {howToUseDialogVisible && <HowToUse visible={howToUseDialogVisible} setVisibility={setHowToUseDialogVisibiliy} videoURL={videoURL}></HowToUse>}
    </>
  );
};
